import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import GenericModal from "components/GenericModal";
import MDBox from "components/MDBox";
import { getCompanyImageUrl } from "utils/helpers/upload";
import { useAppContextController } from "context/AppContext";
import useOpenSeaDragon from "./OpenSeaDragonViewer";

// Extract OpenSeaViewer as a separate memoized component
const OpenSeaViewer = React.memo(({ regions, tileSources }) => {
  const { loadingViewer, viewer } = useOpenSeaDragon({
    regions,
    tileSources,
  });

  // Store the viewer instance in a ref for later use in saving
  React.useEffect(() => {
    if (viewer) {
      // Expose the viewer instance through a data attribute for direct access
      const viewerElement = document.getElementById("viewer");
      if (viewerElement) {
        viewerElement.dataset.viewerReady = "true";
      }
    }
  }, [viewer]);

  return (
    <Box sx={{ width: "100%", height: "600px", position: "relative" }}>
      <div id="viewer" style={{ width: "100%", height: "100%" }} />
      {loadingViewer && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
});

OpenSeaViewer.displayName = "OpenSeaViewer";

const OverlayEditorModal = React.memo(({ open, setOpen, selectedForm, onSave }) => {
  const [saving, setSaving] = useState(false);

  // Prepare data for OpenSeadragon
  const { company } = useAppContextController();
  const IMAGE_SERVER = useMemo(() => getCompanyImageUrl(company), [company]);

  // Function to extract regions from form data
  const extractRegionsFromFormData = useCallback((formData) => {
    const regions = [];
    const { sections = [] } = formData;

    let regionCounter = 0;

    // Process each section's rows and columns to extract field positions
    sections.forEach((section) => {
      const { rows = [] } = section;

      rows.forEach((row) => {
        const { columns = [] } = row;

        columns.forEach((field) => {
          if (field.position) {
            const { page = 1, x, y, width, height, confidence = 0.9 } = field.position;

            // Create a region object with the field data
            regions.push({
              id: `region_${regionCounter}`,
              x,
              y,
              w: width,
              h: height,
              confidence,
              pageNumber: page - 1, // Convert to 0-based
              fieldId: field.id,
              type: field.type || "input",
              name: field.name,
            });

            regionCounter += 1;
          }
        });
      });
    });

    return regions;
  }, []);

  // Use useMemo to prevent unnecessary recalculation
  const openSeaData = useMemo(() => {
    if (!selectedForm || !selectedForm.images) {
      return { regions: [], tileSources: [] };
    }

    // Format the data for useOpenSeaDragon
    const imagePaths = selectedForm.images.paths || [];

    // Extract form fields to create regions
    const formData = selectedForm.form || {};
    const regions = extractRegionsFromFormData(formData);

    // Ensure proper URL formatting for the images
    const formatImageUrl = (path) => {
      // If the path is already a full URL, use it directly
      if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
      }

      // Normalize path to remove leading slash if IMAGE_SERVER ends with slash
      const normalizedPath = path.startsWith("/") ? path.substring(1) : path;
      const baseUrl = IMAGE_SERVER.endsWith("/") ? IMAGE_SERVER : `${IMAGE_SERVER}/`;
      return `${baseUrl}${normalizedPath}`;
    };

    // Create tileSources structure
    const tileSources = imagePaths.map((path) => ({
      type: "image",
      url: formatImageUrl(path),
    }));

    console.log("Prepared tileSources:", tileSources);

    return { regions, tileSources };
  }, [selectedForm, extractRegionsFromFormData, IMAGE_SERVER]);

  const handleSaveOverlays = useCallback(() => {
    if (!selectedForm || !selectedForm.form) return;

    setSaving(true);

    try {
      // Get the overlay elements
      const overlayElements = document.querySelectorAll(".overlay");

      if (overlayElements.length === 0) {
        setSaving(false);
        return;
      }

      // Get the viewer instance
      const viewerElement = document.getElementById("viewer");
      const canvasParent = viewerElement?.querySelector(".openseadragon-canvas");

      if (!canvasParent) {
        setSaving(false);
        return;
      }

      const viewer = canvasParent.parentNode?.__openSeadragonViewer;

      if (!viewer) {
        setSaving(false);
        return;
      }

      // Create a deep copy of the form data
      const updatedFormData = JSON.parse(JSON.stringify(selectedForm.form));

      // Process each overlay element
      overlayElements.forEach((element) => {
        try {
          const { fieldId } = element.dataset;
          const pageNumber = parseInt(element.dataset.pageNumber, 10);

          if (!fieldId) {
            return;
          }

          // Get the current overlay
          const overlay = viewer.getOverlayById(element.id);
          if (!overlay) {
            return;
          }

          // Get the overlay's bounds in viewport coordinates
          const viewportBounds = overlay.bounds;

          // Get the viewer item (page) for this overlay
          const viewerItem = viewer.world.getItemAt(pageNumber - 1);
          if (!viewerItem) {
            return;
          }

          // Get the image size
          const imageSize = viewerItem.getContentSize();

          // Convert viewport coordinates to image coordinates
          const imageRect = viewerItem.viewportToImageRectangle(viewportBounds);

          // Normalize to 0-1 range
          const normalizedPosition = {
            x: imageRect.x / imageSize.x,
            y: imageRect.y / imageSize.y,
            width: imageRect.width / imageSize.x,
            height: imageRect.height / imageSize.y,
          };

          // Update the form data
          updatedFormData.sections = updatedFormData.sections.map((section) => {
            const updatedRows = section.rows?.map((row) => {
              const updatedColumns = row.columns?.map((field) => {
                if (field.id === fieldId && field.position) {
                  return {
                    ...field,
                    position: {
                      ...field.position,
                      x: normalizedPosition.x,
                      y: normalizedPosition.y,
                      width: normalizedPosition.width,
                      height: normalizedPosition.height,
                    },
                  };
                }
                return field;
              });
              return { ...row, columns: updatedColumns };
            });
            return { ...section, rows: updatedRows };
          });
        } catch (err) {
          console.error("Error processing overlay:", err);
        }
      });

      // Call the onSave callback with the updated form data
      if (onSave) {
        onSave(updatedFormData);
      }

      // Close the modal
      setOpen(false);
    } catch (error) {
      console.error("Error saving overlay positions:", error);
    } finally {
      setSaving(false);
    }
  }, [selectedForm, setOpen, onSave]);

  // Destructure the regions and tileSources from the memoized openSeaData
  const { regions, tileSources } = openSeaData;

  return (
    <GenericModal
      open={open}
      setOpen={setOpen}
      header={<Typography variant="h5">Edit Form Overlays</Typography>}
      body={<OpenSeaViewer regions={regions} tileSources={tileSources} />}
      icon="edit"
      iconColor="info"
      width="60%"
      maxHeight="90vh"
      buttons={
        <MDBox sx={{ display: "flex", gap: 2 }}>
          {saving ? (
            <CircularProgress size={24} sx={{ mr: 2 }} />
          ) : (
            <Button variant="contained" color="success" onClick={handleSaveOverlays}>
              Save Changes
            </Button>
          )}
          <Button variant="contained" color="info" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </MDBox>
      }
    />
  );
});

OverlayEditorModal.displayName = "OverlayEditorModal";

export default OverlayEditorModal;
